/* cmp-calc-core: the maths of the Coffee Cost Calculator, ported cell by cell from the free
   xlsx (sheets "Your inputs", "Food Cost per cup", "Retail bag Cost": 51 formulas), English file
   and Italian file. No DOM, no side effects: the page and the test bench use the same code.

   Excel semantics kept on purpose:
   - a reference to an empty input cell is 0 (the shared coffee and milk costs);
   - IF(AND(C<>"",E<>"")...) skips a row when quantity or cost is empty (result ""), 0 is a value;
   - SUM ignores the "" and "check unit" cells;
   - VLOOKUP(unit, table, 2, FALSE) is exact and case-insensitive; an unknown unit gives "check unit".
   The Italian file differs from the English one by VAT: the selling price is entered as on the
   menu list (VAT included) and food cost and margins are computed on the net price. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.CmpCalcCore = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* "Your inputs" E3:G42, unchanged: unit -> [grams or millilitres per unit, kind W(eight)/V(olume)] */
  var UNIT_ROWS = [
    ['g', 1, 'W'], ['gr', 1, 'W'], ['gram', 1, 'W'], ['grams', 1, 'W'],
    ['kg', 1000, 'W'], ['kilo', 1000, 'W'], ['kilos', 1000, 'W'], ['kilogram', 1000, 'W'], ['kilograms', 1000, 'W'],
    ['oz', 28.349523125, 'W'], ['ounce', 28.349523125, 'W'], ['ounces', 28.349523125, 'W'],
    ['lb', 453.59237, 'W'], ['lbs', 453.59237, 'W'], ['pound', 453.59237, 'W'], ['pounds', 453.59237, 'W'],
    ['ml', 1, 'V'], ['milliliter', 1, 'V'], ['millilitre', 1, 'V'],
    ['l', 1000, 'V'], ['liter', 1000, 'V'], ['litre', 1000, 'V'], ['liters', 1000, 'V'], ['litres', 1000, 'V'],
    ['fl oz', 29.5735295625, 'V'], ['floz', 29.5735295625, 'V'], ['fluid ounce', 29.5735295625, 'V'], ['fluid ounces', 29.5735295625, 'V'],
    ['cup', 236.5882365, 'V'], ['cups', 236.5882365, 'V'],
    ['pt', 473.176473, 'V'], ['pint', 473.176473, 'V'],
    ['qt', 946.352946, 'V'], ['quart', 946.352946, 'V'], ['quarts', 946.352946, 'V'],
    ['half gal', 1892.705892, 'V'], ['half gallon', 1892.705892, 'V'],
    ['gal', 3785.411784, 'V'], ['gallon', 3785.411784, 'V'], ['gallons', 3785.411784, 'V']
  ];
  var UNITS = {};
  UNIT_ROWS.forEach(function (r) { UNITS[r[0]] = { base: r[1], kind: r[2] }; });

  /* the lists the file offers in its drop-downs, kept as they are */
  var LISTS = {
    dose: ['g', 'oz', 'ml', 'fl oz', 'kg', 'lb', 'L'],
    coffeeBuy: ['lb', 'kg', 'oz', 'g'],
    milkBuy: ['gal', 'half gal', 'qt', 'L', 'ml', 'fl oz'],
    bagSize: ['oz', 'lb', 'g', 'kg']
  };

  function unit(name) {
    if (typeof name !== 'string') return null;
    return UNITS[name.toLowerCase()] || null;   // VLOOKUP is case-insensitive
  }

  /* an input is either null (empty cell) or a finite number; anything else is not a value */
  function val(x) {
    if (x === null || x === undefined || x === '') return null;
    var n = typeof x === 'number' ? x : NaN;
    return isFinite(n) ? n : null;
  }
  function zero(x) { var v = val(x); return v === null ? 0 : v; }   // reference to an empty cell

  function isMetric(sys) { return String(sys).trim().toUpperCase() === 'METRIC'; }

  var CHECK = 'check unit';

  /* thresholds and status keys: the words live in the page dictionary, the keys are what the tests compare */
  /* Excel and LibreOffice compare the 15-digit decimal, not the binary value: 0.30000000000000004 is 0.3 */
  function snap(x) { return Number(x.toPrecision(15)); }
  function cupStatus(fc) {
    if (fc === null) return '-';
    fc = snap(fc);
    if (fc < 0.2) return 'strong';
    if (fc <= 0.3) return 'healthy';
    if (fc <= 0.38) return 'watch';
    return 'risk';
  }
  function bagStatus(fc) {
    if (fc === null) return '-';
    fc = snap(fc);
    if (fc <= 0.25) return 'strong';
    if (fc <= 0.35) return 'healthy';
    if (fc <= 0.45) return 'watch';
    return 'risk';
  }

  /* state:
     { lang:'en'|'it', sys:'US'|'Metric', coffeeCost, coffeeUnit, milkCost, milkUnit, targetFc,
       cup:{ rows:[{qty,unit,cost}] (10), servings, multiplier, price, vat },
       bag:{ size, sizeUnit, price, vat, items:[{cost,qty}] (8) } }
     Numbers are numbers or null. lang 'it' switches on the VAT formulas. */
  function compute(s) {
    var it = s.lang === 'it';
    var metric = isMetric(s.sys);
    var coffeeCost = zero(s.coffeeCost), milkCost = zero(s.milkCost);
    var out = { cup: {}, bag: {} };

    /* ---------- Food Cost per cup ---------- */
    var rows = s.cup.rows, cost = [], total = 0;
    for (var i = 0; i < 10; i++) {
      var r = rows[i] || {};
      var q = val(r.qty), c;
      var baseName;
      if (i === 0) { c = coffeeCost; baseName = s.coffeeUnit; }        // E7 = 'Your inputs'!B7
      else if (i === 1) { c = milkCost; baseName = s.milkUnit; }       // E8 = 'Your inputs'!B8
      else {
        c = val(r.cost);
        var u0 = unit(r.unit);
        baseName = u0 ? (u0.kind === 'W' ? (metric ? 'kg' : 'lb') : (metric ? 'L' : 'gal')) : null;
      }
      var line;
      if (q === null || c === null) line = '';                         // IF(AND(C<>"",E<>""), .., "")
      else {
        var ur = unit(r.unit), ub = baseName === null ? null : unit(baseName);
        line = (ur && ub) ? q * ur.base / ub.base * c : CHECK;         // IFERROR(.., "check unit")
      }
      cost.push(line);
      if (typeof line === 'number') total += line;                     // SUM ignores text
    }
    out.cup.lines = cost;
    out.cup.total = total;                                             // G17
    var servings = val(s.cup.servings);
    var perServing = (servings !== null && servings > 0) ? total / servings : null;   // G18
    out.cup.perServing = perServing;
    var target = zero(s.targetFc);                                     // C22 = 'Your inputs'!B9
    var mult = val(s.cup.multiplier), price = val(s.cup.price);
    var vatF = it ? 1 + zero(s.cup.vat) / 100 : 1;
    out.cup.suggestedFromTarget = (target > 0 && perServing !== null) ? perServing / (target / 100) * vatF : null;
    out.cup.suggestedFromMultiplier = (mult !== null && mult > 0 && perServing !== null) ? perServing * mult * vatF : null;
    var hasPrice = price !== null && price > 0 && perServing !== null;
    out.cup.foodCost = hasPrice ? perServing * vatF / price : null;
    out.cup.margin = hasPrice ? 1 - perServing * vatF / price : null;
    out.cup.status = cupStatus(out.cup.foodCost);

    /* ---------- Retail bag Cost ---------- */
    var size = val(s.bag.size), bagPrice = val(s.bag.price);
    var qtyCoffee;
    if (size === null) qtyCoffee = '';
    else {
      var us = unit(s.bag.sizeUnit), ub2 = unit(s.coffeeUnit);
      qtyCoffee = (us && ub2) ? size * us.base / ub2.base : CHECK;     // E12
    }
    out.bag.coffeeQty = qtyCoffee;
    var f12 = (typeof qtyCoffee === 'number') ? coffeeCost * qtyCoffee : '';   // C12 = B7, never empty
    var bagTotal = typeof f12 === 'number' ? f12 : 0;
    var items = [];
    for (var j = 0; j < 8; j++) {
      var it_ = s.bag.items[j] || {};
      var ci = val(it_.cost), qi = val(it_.qty);
      var f = (ci === null || qi === null) ? '' : ci * qi;
      items.push(f);
      if (typeof f === 'number') bagTotal += f;
    }
    out.bag.coffeeCost = f12;                                          // F12
    out.bag.items = items;                                             // F13:F20
    out.bag.total = bagTotal;                                          // F21
    var vatB = it ? 1 + zero(s.bag.vat) / 100 : 1;
    /* F12 = C12*"check unit" is #VALUE! in the file and F21, C24.. inherit it: same here */
    out.bag.error = qtyCoffee === CHECK ? CHECK : null;
    var okBag = bagPrice !== null && bagPrice > 0 && !out.bag.error;
    var netPrice = okBag ? bagPrice / vatB : null;
    out.bag.foodCost = okBag ? bagTotal / netPrice : null;             // C24
    out.bag.margin = okBag ? netPrice - bagTotal : null;               // C25
    out.bag.marginPct = okBag ? 1 - bagTotal / netPrice : null;        // C26
    out.bag.status = bagStatus(out.bag.foodCost);
    return out;
  }

  /* ---------- display ---------- */

  /* Excel and LibreOffice round the 15-digit decimal form of a number half away from zero; toFixed
     rounds the binary value (1.005 -> "1.00"), which would show a different last digit. */
  function round(x, d) {
    var sign = x < 0 ? -1 : 1;
    var y = Number(Math.abs(x).toPrecision(15));
    if (y < 1e-6) return 0;                       // 0 plus floating-point noise (selling at cost): the sheet shows 0
    if (y >= 1e15) return sign * y;               // beyond the 15 digits there is nothing left to round
    var n = Math.round(Number(y.toPrecision(15) + 'e' + d));
    return sign * Number(n + 'e-' + d);
  }
  function fixed(x, d) {
    var r = round(x, d);
    var t = Math.abs(r).toFixed(d);
    return (r < 0 && Number(t) !== 0 ? '-' : '') + t;
  }
  /* fmt: 'n3' -> #,##0.000, 'n2' -> #,##0.00, 'p1' -> 0.0%, lang decides the separators */
  function fmt(x, kind, lang) {
    if (x === null || x === undefined || x === '') return '';
    if (typeof x !== 'number' || !isFinite(x)) return String(x);
    if (Math.abs(x) >= 1e15) return '';           // out of range: shown as a dash by the page
    var pct = kind.charAt(0) === 'p', d = Number(kind.slice(1));
    var t = fixed(pct ? x * 100 : x, d);
    var neg = t.charAt(0) === '-'; if (neg) t = t.slice(1);
    var parts = t.split('.');
    /* the file's percent format (0.0%) has no thousands separator, the money formats (#,##0.00) do */
    var intp = pct ? parts[0] : parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, lang === 'it' ? '.' : ',');
    var res = intp + (parts[1] ? (lang === 'it' ? ',' : '.') + parts[1] : '');
    return (neg ? '-' : '') + res + (pct ? '%' : '');
  }

  /* a number typed by a person: 1.30, 1,30, 1 234,50, 1,234.50, 12,25 -> number; '' -> null; junk -> NaN.
     A lone separator followed by exactly three digits is a thousands mark only where the language groups with
     it (comma in English, dot in Italian) and only when the number does not start with 0: "0,250" is 0.25. */
  function parseNumber(str, lang) {
    if (typeof str === 'number') return isFinite(str) ? str : NaN;
    if (str === null || str === undefined) return null;
    var t = String(str).replace(/[\s\u00a0\u202f]/g, '');
    if (t === '') return null;
    if (!/^[+-]?[\d.,]+$/.test(t)) return NaN;
    var sign = '';
    if (t.charAt(0) === '+' || t.charAt(0) === '-') { sign = t.charAt(0) === '-' ? '-' : ''; t = t.slice(1); }
    if (t === '' || !/\d/.test(t)) return NaN;
    var grouped = function (u, sep) { return new RegExp('^[1-9]\\d{0,2}(\\' + sep + '\\d{3})+$').test(u); };
    var lastDot = t.lastIndexOf('.'), lastCom = t.lastIndexOf(',');
    var out;
    if (lastDot >= 0 && lastCom >= 0) {                                 // both: the last one is the decimal mark
      var dec = lastDot > lastCom ? '.' : ',', grp = dec === '.' ? ',' : '.';
      var parts = t.split(dec);
      if (parts.length !== 2 || !/^\d*$/.test(parts[1])) return NaN;
      var ip = parts[0];
      if (!(grouped(ip, grp) || /^\d{1,3}$/.test(ip) && ip.indexOf(grp) < 0)) return NaN;
      out = ip.split(grp).join('') + '.' + parts[1];
    } else if (lastCom >= 0 || lastDot >= 0) {
      var sep = lastCom >= 0 ? ',' : '.';
      var n = t.split(sep).length - 1;
      var groupsHere = (sep === ',' && lang !== 'it') || (sep === '.' && lang === 'it');
      if (n > 1) { if (!grouped(t, sep)) return NaN; out = t.split(sep).join(''); }
      else if (groupsHere && grouped(t, sep)) out = t.split(sep).join('');
      else out = t.replace(sep, '.');
    } else out = t;
    var v = Number(sign + out);
    return isFinite(v) ? v : NaN;
  }

  /* a number written into the address bar or an input: plain decimals, never 1e-7 */
  function plain(n) {
    var s = String(n);
    if (/e/i.test(s)) s = n.toFixed(12).replace(/0+$/, '').replace(/\.$/, '');
    return s;
  }

  return {
    UNITS: UNITS, LISTS: LISTS, CHECK: CHECK,
    compute: compute, fmt: fmt, round: round, parseNumber: parseNumber, plain: plain, unit: unit,
    cupStatus: cupStatus, bagStatus: bagStatus
  };
});
